import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { id: "anasayfa", label: "Anasayfa" },
  { id: "hakkimizda", label: "Hakkımızda" },
  { id: "urunler", label: "Ürünler" },
  { id: "referanslar", label: "Referanslar" },
  { id: "ekibimiz", label: "Ekibimiz" },
  { id: "iletisim", label: "İletişim" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("anasayfa");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      const sections = links.map((l) => document.getElementById(l.id));
      const y = window.scrollY + 160;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.offsetTop <= y) {
          setActive(links[i].id);
          break;
        }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-3 inset-x-3 sm:inset-x-6 z-40 transition-all duration-500 rounded-full ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border border-gray-200/70 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.1)]"
          : "bg-white/40 backdrop-blur-md border border-white/30"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <button
            data-testid="navbar-logo"
            onClick={() => handleClick("anasayfa")}
            className="group flex items-center gap-2.5"
          >
            <div className="w-9 h-9 bg-[#FF5A00] grid place-items-center rounded-xl shadow-[0_6px_18px_-6px_rgba(255,90,0,0.65)] group-hover:scale-105 transition-transform">
              <span className="font-display text-white text-base font-bold">İ</span>
            </div>
            <div className="text-left leading-tight">
              <div className="font-display text-[15px] font-semibold tracking-tight text-gray-900">
                İnci Tel
              </div>
              <div className="font-mono-disp text-[9px] uppercase tracking-[0.15em] text-gray-500 -mt-0.5">
                Çit Sistemleri
              </div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`navbar-link-${l.id}`}
                onClick={() => handleClick(l.id)}
                className={`nav-link font-body text-sm font-medium ${
                  active === l.id ? "text-[#FF5A00] active" : "text-gray-700 hover:text-gray-900"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <a
              href="tel:+905313185280"
              data-testid="navbar-phone"
              className="font-mono-disp text-xs font-medium text-gray-600 hover:text-[#FF5A00] transition-colors px-3 py-2"
            >
              +90 531 318 52 80
            </a>
            <button
              data-testid="navbar-cta"
              onClick={() => handleClick("iletisim")}
              className="btn-mod btn-primary btn-shift !py-2 !px-4 text-xs"
            >
              Teklif Al
              <ArrowUpRight className="arrow w-3.5 h-3.5" />
            </button>
          </div>

          <button
            data-testid="navbar-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Menüyü aç/kapat"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white border-t border-gray-100 rounded-b-3xl overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`navbar-mobile-link-${l.id}`}
                  onClick={() => handleClick(l.id)}
                  className={`text-left font-body text-base font-medium py-1.5 ${
                    active === l.id ? "text-[#FF5A00]" : "text-gray-900"
                  }`}
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="navbar-mobile-cta"
                onClick={() => handleClick("iletisim")}
                className="mt-2 btn-mod btn-primary btn-shift text-sm"
              >
                Teklif Al <ArrowUpRight className="arrow w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
